import { graphql } from 'gatsby';
import React from 'react';
import { Helmet } from 'react-helmet';
import Seo from '../../components/SEO';
import FaqBanner from '../../components/FaqBanner/FaqBanner';
import FaqBody from '../../components/FaqBody/FaqBody';
import FaqCover from '../../components/FaqCover/FaqCover';
import FaqLayout from '../../components/FaqLayout/FaqLayout';
import HeaderTheme from '../../components/Header/HeaderTheme';
import Promotions from '../../components/Promotions/Promotions';
import usePromotions from '../../hooks/usePromotions';
import flatListToHierarchical from '../../utils/flatListToHierarchical';

const FaqTemplate = ({
  data: {
    page,
    relatedFaqs
  },
  location: { pathname }
}) => {
  const { 
    title, 
    sectionContent, 
    relatedServices, 
    categories, 
    sectionPromotions,
    seo,
    metadata
  } = page;
  const cats = flatListToHierarchical(categories.nodes);
  const promotions = usePromotions(sectionPromotions.promotions, relatedServices.services, {
    excludePath: pathname
  });
  let htmlString = '';

  if (sectionContent.body) {
    htmlString = sectionContent.body
      .filter((item) => item.fieldGroupName !== 'WpFaq_Sectioncontent_Body_LayoutImage')
      .map((item) => item.wysiwyg)
      .join('');
  }

  htmlString = JSON.stringify(htmlString).replace(/\\n/g, '');

  return (
    <>
      <HeaderTheme />
      
      <Seo 
        title={`FAQ: ${seo.title}`} 
        description={metadata.metaDescription} 
        image={metadata.metaImage}
        seo={seo}
      />

      <Helmet>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "${title}",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": ${htmlString}
                  }
                }
              ]
            }
          `}
        </script>
      </Helmet>

      <FaqCover />
      <FaqLayout categoryId={cats[ 0 ]?.id}>
        <FaqBody 
          title={title} 
          body={sectionContent.body} 
          relatedServices={relatedServices.services} 
          relatedNodes={relatedFaqs.nodes}/>
      </FaqLayout>
      <FaqBanner />
      <Promotions items={promotions} />
    </>
  );
};

export default FaqTemplate;

export const query = graphql`
  query FaqTemplate($id: String!, $faqCatIds: [String!]) {
    page: wpFaq(id: { eq: $id }) {
      nodeType
      title
      uri
      seo {
          title
          metaDesc
          focuskw
          metaKeywords
          metaRobotsNoindex
          metaRobotsNofollow
          opengraphTitle
          opengraphDescription
          opengraphImage {
              altText
              sourceUrl
              srcSet
          }
          twitterTitle
          twitterDescription
          twitterImage {
              altText
              sourceUrl
              srcSet
          }
          canonical
          cornerstone
          schema {
              articleType
              pageType
              raw
          }
      }
      metadata {
        metaDescription
        metaImage {
          ...WpImage
        }
      }
      sectionContent {
        body {
          ... on WpFaq_Sectioncontent_Body_LayoutWysiwyg {
            fieldGroupName
            wysiwyg
          }
          ... on WpFaq_Sectioncontent_Body_LayoutImage {
            fieldGroupName
            image {
              ...WpImage
            }
          }
          ... on WpFaq_Sectioncontent_Body_LayoutVideo {
            fieldGroupName
            caption
            video
          }
        }
      }
      relatedServices {
        services {
          ...ServiceCard
        }
      }
      categories: faqCategories {
        nodes {
          id
          parentId
          name
          uri
        }
      }
      sectionPromotions {
        promotions {
          promotion {
            ...Promotion
          }
        }
      }
    }
    relatedFaqs: allWpFaq(
      filter: {
        id: { ne: $id }
        faqCategories: { nodes: { elemMatch: { id: { in: $faqCatIds } } } }
      }
      sort: { modifiedGmt: DESC }
      limit: 99
    ) {
      nodes {
        title
        uri
        id
      }
    }
  }
`;
